/**
 * JEE Main NTA percentile (normalisation) arithmetic.
 *
 * Source: National Testing Agency, "Procedure to be adopted for compilation of NTA scores for
 * multi session Papers (Normalization procedure based on PERCENTILE SCORE)", published on the
 * JEE (Main) site (https://jeemain.nta.nic.in/document/national-testing-agencyntaprocedure-to-be-adopted-for-compilation-of-nta-scoresfor-multi-sessionpapersnormalization-procedure-based-on-percentilescore/;
 * PDF at https://cdnbbsr.s3waas.gov.in/s3f8e59f4b2fe7c5705bf878bbd494ccdf/uploads/2022/01/2022012130.pdf).
 * Retrieved 2026-10-16. The document gives:
 *
 *   Percentile = 100 x (number of candidates appeared in the session with raw score EQUAL TO
 *   OR LESS than the candidate) / (total number of candidates appeared in the session).
 *
 * Percentiles are "calculated up to 7 decimal places to avoid bunching effect and reduce
 * ties", the highest raw score in each session gets 100, and "the Percentile of the Total
 * shall NOT be an aggregate or average of the Percentile of individual subject".
 *
 * The rank conversions below are our own arithmetic on that definition, not an NTA formula:
 * they ignore ties and the merging of sessions, so they are approximations.
 */

/** Whole-number counts: 1 <= atOrBelow <= appeared. */
export function isValidCounts(
  appeared: number | null,
  atOrBelow: number | null
): boolean {
  return (
    appeared !== null &&
    atOrBelow !== null &&
    Number.isInteger(appeared) &&
    Number.isInteger(atOrBelow) &&
    appeared >= 1 &&
    atOrBelow >= 1 &&
    atOrBelow <= appeared
  );
}

/** NTA percentile from counts, to full precision. */
export function percentileFromCounts(
  appeared: number,
  atOrBelow: number
): number {
  return (100 * atOrBelow) / appeared;
}

export function isPercentile(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value >= 0 && value <= 100;
}

export function isPositiveCount(value: number | null): value is number {
  return value !== null && Number.isInteger(value) && value >= 1;
}

/** Candidates with a strictly higher raw score than a given percentile implies. */
export function candidatesAbove(percentile: number, appeared: number): number {
  return (appeared * (100 - percentile)) / 100;
}

/** Approximate rank: candidates above, plus one. Ties would share a rank. */
export function approximateRank(percentile: number, appeared: number): number {
  return Math.max(1, Math.round(candidatesAbove(percentile, appeared)) + 1);
}

/** Approximate percentile of a rank among `appeared` candidates, assuming no ties. */
export function approximatePercentile(rank: number, appeared: number): number {
  return (100 * (appeared - rank + 1)) / appeared;
}

export function isRank(rank: number | null, appeared: number | null): boolean {
  return (
    rank !== null &&
    appeared !== null &&
    Number.isInteger(rank) &&
    Number.isInteger(appeared) &&
    rank >= 1 &&
    appeared >= 1 &&
    rank <= appeared
  );
}
